import app from 'flarum/forum/app';
import highlight from 'flarum/common/helpers/highlight';
import MentionableModel from 'ext:flarum/mentions/forum/mentionables/MentionableModel';
import TopicHeat from '../components/TopicHeat';
import { currentTopicSettings } from '../state/topicSettings';
import { usedTopicCount } from '../utils/topicUsage';

/**
 * Offers existing topics in the composer's `#` dropdown, alongside
 * flarum/tags' tag suggestions.
 *
 * This is the piece that makes freeform topics cohere instead of fragment:
 * without it, #gameday, #GameDay and #game-day become three separate feeds
 * within a week because nobody can see what already exists while they type.
 *
 * Registered onto the EXISTING HashMentionFormat rather than a new format —
 * core declares that format `extendable`, so `#` keeps one dropdown showing
 * tags and topics together instead of two competing triggers.
 */
export default class TopicMention extends MentionableModel {
  type() {
    return 'topic';
  }

  /**
   * Nothing is offered before the user types. The store holds whatever
   * topics previous pages happened to load, which is an arbitrary set — a
   * dropdown of arbitrary topics on a bare `#` is noise, and worse, it would
   * push the tag suggestions (which ARE meaningful on a bare `#`) out of view.
   */
  initialResults() {
    return [];
  }
  async search(typed) {
    return await app.store.find('topics', {
      filter: {
        q: typed
      },
      page: {
        limit: 5
      },
      // By heat, not by post count: the point of the suggestion list is to
      // show which topics are alive right now.
      sort: '-heatScore'
    });
  }

  /**
   * The dropdown filters the accumulated result set client-side as more is
   * typed. Prefix match on the folded key, mirroring the server's LIKE 'x%'.
   */
  matches(model, typed) {
    if (!typed) return false;
    return model.nameKey().substr(0, typed.length) === typed.toLowerCase();
  }

  /**
   * Insert what the topic is actually called, not what was typed — picking
   * "#GameDay" from the list should write "#GameDay".
   */
  replacement(model) {
    return this.format.format(model.name());
  }

  /**
   * null = don't cap store-matched results. The set only ever contains what
   * our own search() returned (initialResults is empty), which is already
   * limited to 5 server-side.
   */
  maxStoreMatchedResults() {
    return null;
  }
  suggestion(model, typed) {
    return m('[', null, m("span", {
      className: "TopicMention-icon",
      "aria-hidden": "true"
    }, "#"), m("span", {
      className: "taglink TopicMention-name"
    }, typed ? highlight(model.name(), typed) : model.name()), m("span", {
      className: "TopicMention-heat"
    }, m(TopicHeat, {
      level: model.heatLevel(),
      score: model.heatScore()
    })));
  }

  /**
   * Whether topics may be offered at all.
   *
   * Two ways to say no: the feature is switched off (no topics per post), or
   * the post already carries as many topics as it may. In the second case
   * the dropdown stops offering topics altogether — the composer says "remove
   * one first" instead, because offering a sixth topic that the server will
   * render as plain text would be a lie.
   */
  enabled() {
    const max = currentTopicSettings().maxPerPost;
    if (max <= 0) return false;
    return usedTopicCount(this.composerContent()) < max;
  }

  /**
   * What is in the composer right now.
   */
  composerContent() {
    return app.composer?.fields?.content?.() ?? '';
  }
}
flarum.reg.add('ziiven-topics', 'forum/mentionables/TopicMention', TopicMention);